import React, {useRef, useContext, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import {NAVBAR_HEIGHT, PAGE_WIDTH} from '../../../shared/style/dimensions';
import useStickyTracking from '../../../shared/utils/hooks/sticky';
import {NavigationContext} from '../../../shared/enhancers/router-enhancer';
import {RenderItems, GridItem} from '../components/shared/render';
import QuestionPanelLarge from '../components/questions-answers/question-panel-large';
import {withCurrentUser} from '../../../shared/enhancers/current-user-enhancer';
import {compose} from 'react-apollo';
import {withAnalyticsPayload} from '../../../shared/enhancers/analytics-enhancer';
import {SIGN_IN_PATH} from '../../../shared/constants/paths';
import {PrivateModeContext} from './../../../shared/enhancers/private-mode-enchancer';
import {adProperty, AD_LAYER, AD_CATEGORY, AD_FUNCTION} from '../../../shared/utils/ad';
import Heading from '../dummy-components/heading';
import NameCard from '../dummy-components/name-card';
import ContentCard from '../dummy-components/content-card';
import Decisions from '../dummy-components/decisions';
import ProsCons from '../dummy-components/pros-cons';
import {conditions, toolConditions} from '../constants/conditions';
import Advertisement from '../dummy-components/advertisement';
import ExtraComponents1 from '../dummy-components/extra-components-1';
import ExtraComponents2 from '../dummy-components/extra-components-2';
import EndComponents from '../dummy-components/end-components';
import SourceStatsNew from '../../../shared/library/cards/source-stats/new';
import ReleaseInfo from '../../../shared/library/cards/stackup/release-info';
import StackupsVulnerabilities from '../../../shared/library/cards/stackup/vulnerabilities';
import {CHARCOAL} from '../../../shared/style/colors';
import GithubStatsCard from '../../../shared/library/cards/github-stats';
import {BASE_TEXT} from '../../../shared/style/typography';

const GET_ADVICE = 'getadvice';

const GRID_WIDTH = 860;

const Page = glamorous.div({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column'
});

const Content = glamorous.main({
  display: 'flex',
  flexDirection: 'column',
  width: PAGE_WIDTH,
  height: '100%'
});

const AdLayout = glamorous.div({
  display: 'flex',
  position: 'relative',
  marginBottom: 50,
  marginTop: 25
});

const Grid = glamorous.div({
  display: 'grid',
  width: GRID_WIDTH,
  gridTemplateColumns: `${GRID_WIDTH / 3}px ${GRID_WIDTH / 3}px ${GRID_WIDTH / 3}px`,
  flexGrow: 1
});

const Aside = glamorous.aside({
  display: 'flex'
});

const StatsTitle = glamorous.div({
  ...BASE_TEXT,
  fontSize: 16,
  color: CHARCOAL,
  marginBottom: 10,
  fontWeight: 700
});

const num = 3;

const Stackup = ({
  slug,
  stackup,
  tools,
  signin,
  userId,
  contentGroup,
  currentUser,
  routeAction,
  signupAction,
  setSignupAction
}) => {
  const navigate = useContext(NavigationContext);
  const stickyRef = useRef(null);
  const isStuck = useStickyTracking(stickyRef, NAVBAR_HEIGHT);
  const [openAdviceModal, setOpenAdviceModal] = useState(routeAction === GET_ADVICE);
  const privateMode = useContext(PrivateModeContext);

  const {
    stackupPath,
    contactEnabledTools,
    analyticsData,
    showJobs,
    showPosts,
    showDecisions,
    jobsCount,
    showLoggedOutCta,
    showPros,
    showCons,
    showIntegrations,
    showAlternativeTools,
    removeAfterAlternatives,
    removeAfterCons,
    showOfficialArticles,
    toolsLoaded,
    showGithubRepos,
    showPrivateStacksComparision,
    showTeamComparision,
    showCompanyUsing,
    showTopContent,
    showBottomContent,
    removeAfterDecisions,
    showLoggedOutIntegrationsCta,
    stackupForPackages,
    showVulnerabilities,
    showReleaseInfo,
    showStats
  } = useMemo(() => {
    const obj = {};

    obj.toolsLoaded = tools && tools.every(tool => !tool.loading);

    obj.analyticsData = stackup && {
      name: stackup.title,
      slug: slug,
      contentGroup: contentGroup
    };

    conditions.map(item => {
      obj[item.name] = item.condition({
        currentUser: currentUser,
        stackup: stackup,
        privateMode: privateMode,
        toolsLoaded: obj.toolsLoaded,
        slug: slug
      });
    });

    tools.map(tool => {
      toolConditions.map(item => {
        if (!obj[item.name]) {
          obj[item.name] = item.condition({
            tool: tool,
            currentUser: currentUser,
            stackup: stackup,
            privateMode: privateMode,
            toolsLoaded: obj.toolsLoaded,
            slug: slug
          });
        }
      });
    });

    return obj;
  }, [stackup, tools]);

  const adLayer = adProperty(tools[0], AD_LAYER);
  const adCategory = adProperty(tools[0], AD_CATEGORY);
  const adFunction = adProperty(tools[0], AD_FUNCTION);

  return (
    <Page>
      <Heading
        stackup={stackup}
        tools={tools}
        stickyRef={stickyRef}
        removeAfterCons={removeAfterCons}
        removeAfterAlternatives={removeAfterAlternatives}
        showJobs={showJobs}
        jobsCount={jobsCount}
        showDecisions={showDecisions}
        showPros={showPros}
        showCons={showCons}
        showAlternativeTools={showAlternativeTools}
        showIntegrations={showIntegrations}
        showPosts={showPosts}
        isStuck={isStuck}
        navigate={navigate}
        contactEnabledTools={contactEnabledTools}
        stackupPath={stackupPath}
        tooltip={true}
        currentUser={currentUser}
        privateMode={privateMode}
        userId={userId}
        setOpenAdviceModal={setOpenAdviceModal}
        setSignupAction={setSignupAction}
        toolsLoaded={toolsLoaded}
      />

      <Content>
        <AdLayout>
          <Grid>
            <GridItem span={num} style={{border: 0, padding: '0 10px 0 10px'}}>
              {stackup && !privateMode && (
                <div style={{marginBottom: 15}}>
                  <QuestionPanelLarge
                    hasBorder={true}
                    userId={userId}
                    panelPosition="top"
                    tools={tools}
                    stackupName={stackup.title}
                    stackupSlug={slug}
                    onClick={() => {
                      if (userId) {
                        setOpenAdviceModal(true);
                      } else {
                        setSignupAction(GET_ADVICE);
                        navigate(SIGN_IN_PATH);
                      }
                    }}
                  />
                </div>
              )}
            </GridItem>

            <NameCard
              tools={tools}
              navigate={navigate}
              isStuck={isStuck}
              contactEnabledTools={contactEnabledTools}
              stackupPath={stackupPath}
              currentUser={currentUser}
            />

            <ContentCard stackup={stackup} num={num} />
            {stackupForPackages && (
              <>
                {showStats && (
                  <RenderItems tools={tools} num={num}>
                    {tool => (
                      <>
                        <StatsTitle>{`${tool.name} Stats`}</StatsTitle>
                        <SourceStatsNew
                          // dependentReposCount={tool.dependentReposCount}
                          dependentPackagesCount={tool.dependentPackagesCount}
                          // membersCount={tool.membersCount}
                        />
                      </>
                    )}
                  </RenderItems>
                )}
                {showVulnerabilities && (
                  <RenderItems tools={tools} num={num}>
                    {tool => (
                      <>
                        <StatsTitle>{`${tool.name} Vulnerabilities`}</StatsTitle>
                        <StackupsVulnerabilities vulnerabilities={tool.vulnerabilities.edges} />
                      </>
                    )}
                  </RenderItems>
                )}
                {showReleaseInfo && (
                  <RenderItems tools={tools} num={num}>
                    {tool => (
                      <>
                        <StatsTitle>{`${tool.name} Release info`}</StatsTitle>
                        <ReleaseInfo
                          latestVersionNumber={tool.latestVersionNumber}
                          license={tool.license}
                        />
                      </>
                    )}
                  </RenderItems>
                )}

                {!!(showGithubRepos && removeAfterAlternatives) && removeAfterCons && (
                  <RenderItems tools={tools} num={num}>
                    {tool => (
                      <GithubStatsCard
                        url={tool.pressUrl}
                        stars={tool.githubStarsCount}
                        forks={tool.githubForksCount}
                        lastUpdated={tool.githubUpdatedAt}
                      />
                    )}
                  </RenderItems>
                )}
              </>
            )}

            <Decisions
              num={num}
              stackup={stackup}
              privateMode={privateMode}
              tools={tools}
              slug={slug}
              stackupForPackages={stackupForPackages}
            />

            {removeAfterDecisions && !showTopContent && !showBottomContent && (
              <ProsCons
                showPros={showPros}
                showCons={showCons}
                num={num}
                tools={tools}
                privateMode={privateMode}
                analyticsData={analyticsData}
                showLoggedOutCta={showLoggedOutCta}
              />
            )}

            {removeAfterDecisions && !showTopContent && !showBottomContent && (
              <ExtraComponents1
                removeAfterCons={removeAfterCons}
                removeAfterAlternatives={removeAfterAlternatives}
                num={num}
                tools={tools}
                stackup={stackup}
                privateMode={privateMode}
                userId={userId}
                slug={slug}
                setOpenAdviceModal={setOpenAdviceModal}
                setSignupAction={setSignupAction}
                navigate={navigate}
                showJobs={showJobs}
                currentUser={currentUser}
                analyticsData={analyticsData}
                showIntegrations={showIntegrations}
                showGithubRepos={showGithubRepos}
                showPrivateStacksComparision={showPrivateStacksComparision}
                showTeamComparision={showTeamComparision}
                showCompanyUsing={showCompanyUsing}
                showLoggedOutIntegrationsCta={showLoggedOutIntegrationsCta}
                stackupForPackages={stackupForPackages}
              />
            )}
            {removeAfterDecisions && (
              <ExtraComponents2
                stackup={stackup}
                num={num}
                slug={slug}
                contentGroup={contentGroup}
                adLayer={adLayer}
                adCategory={adCategory}
                adFunction={adFunction}
                removeAfterCons={removeAfterCons}
                showPosts={showPosts}
                removeAfterAlternatives={removeAfterAlternatives}
                currentUser={currentUser}
                showAlternativeTools={showAlternativeTools}
                tools={tools}
                showOfficialArticles={showOfficialArticles}
                privateMode={privateMode}
                showTopContent={showTopContent}
                showBottomContent={showBottomContent}
              />
            )}
          </Grid>

          <Aside>
            {stackup && (
              <Advertisement
                stackup={stackup}
                tools={tools}
                slug={slug}
                contentGroup={contentGroup}
                privateMode={privateMode}
                adLayer={adLayer}
                adCategory={adCategory}
                adFunction={adFunction}
              />
            )}
          </Aside>
        </AdLayout>
      </Content>
      <EndComponents
        stackup={stackup}
        signin={signin}
        setSignupAction={setSignupAction}
        signupAction={signupAction}
        slug={slug}
        toolsLoaded={toolsLoaded}
        openAdviceModal={openAdviceModal}
        tools={tools}
        setOpenAdviceModal={setOpenAdviceModal}
      />
    </Page>
  );
};

Stackup.propTypes = {
  userId: PropTypes.any,
  slug: PropTypes.string,
  stackup: PropTypes.object,
  tools: PropTypes.array,
  signin: PropTypes.bool,
  contentGroup: PropTypes.string,
  currentUser: PropTypes.object,
  routeAction: PropTypes.string,
  signupAction: PropTypes.string,
  setSignupAction: PropTypes.func
};

export default compose(
  withCurrentUser,
  withAnalyticsPayload({
    'page.name': 'Stackups',
    path: typeof window !== 'undefined' ? window.location.pathname : null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    referrer: typeof document !== 'undefined' ? document.referrer : null
  })
)(Stackup);
